<!--
  International Auctions — index (F1.1, BXS-3627).

  Angular 20 built-in control flow only: no <ng-container> / <ng-template>
  anywhere, so the BXS-3584 template guard has nothing to catch, and every
  [class.*] / [attr.*] binding sits on a real element.
-->

<!-- ── Hero band ────────────────────────────────────────────────────────── -->
<section class="ia-index__hero">
  <div class="ia-index__hero-inner">
    <a class="ia-index__back" [routerLink]="'/' | localeLink">
      <span class="ia-index__back-arrow" aria-hidden="true">&#8592;</span>
      <span>{{ 'international_auctions.back_to_homepage' | translate }}</span>
    </a>

    <h1 class="ia-index__title">
      {{ 'international_auctions.title' | translate }}
    </h1>

    <p class="ia-index__standfirst">
      {{ 'international_auctions.standfirst' | translate }}
    </p>
  </div>
</section>

<!-- ── Filter bar ───────────────────────────────────────────────────────── -->
<section class="ia-index__filters">
  <div class="ia-index__filters-inner">
    <!-- House -->
    <div class="ia-index__group">
      <span class="ia-index__group-label" id="ia-filter-house">
        {{ 'international_auctions.filters.house' | translate }}
      </span>
      <div class="ia-index__pills" role="group" aria-labelledby="ia-filter-house">
        <button
          type="button"
          class="ia-index__pill"
          [class.is-active]="!house"
          [attr.aria-pressed]="!house"
          (click)="selectHouse(null)"
        >
          {{ 'international_auctions.filters.all' | translate }}
        </button>
        @for (option of houses; track option.id) {
          <button
            type="button"
            class="ia-index__pill"
            [class.is-active]="house === option.slug"
            [attr.aria-pressed]="house === option.slug"
            (click)="selectHouse(option.slug)"
          >
            {{ houseLabel(option) }}
          </button>
        }
      </div>
    </div>

    <!-- Status -->
    <div class="ia-index__group">
      <span class="ia-index__group-label" id="ia-filter-status">
        {{ 'international_auctions.filters.status' | translate }}
      </span>
      <div class="ia-index__pills" role="group" aria-labelledby="ia-filter-status">
        <button
          type="button"
          class="ia-index__pill"
          [class.is-active]="!status"
          [attr.aria-pressed]="!status"
          (click)="selectStatus(null)"
        >
          {{ 'international_auctions.filters.all' | translate }}
        </button>
        @for (option of statuses; track option) {
          <button
            type="button"
            class="ia-index__pill"
            [class.is-active]="status === option"
            [attr.aria-pressed]="status === option"
            (click)="selectStatus(option)"
          >
            {{ statusKey(option) | translate }}
          </button>
        }
      </div>
    </div>

    <!-- Sale type -->
    <div class="ia-index__group">
      <span class="ia-index__group-label" id="ia-filter-type">
        {{ 'international_auctions.filters.sale_type' | translate }}
      </span>
      <div class="ia-index__pills" role="group" aria-labelledby="ia-filter-type">
        <button
          type="button"
          class="ia-index__pill"
          [class.is-active]="!saleType"
          [attr.aria-pressed]="!saleType"
          (click)="selectSaleType(null)"
        >
          {{ 'international_auctions.filters.all' | translate }}
        </button>
        @for (option of saleTypes; track option.id) {
          <button
            type="button"
            class="ia-index__pill"
            [class.is-active]="saleType === option.slug"
            [attr.aria-pressed]="saleType === option.slug"
            (click)="selectSaleType(option.slug)"
          >
            {{ saleTypeLabel(option) }}
          </button>
        }
      </div>
    </div>

    <button type="button" class="ia-index__reset" (click)="resetFilters()">
      {{ 'international_auctions.filters.reset' | translate }}
    </button>
  </div>
</section>

<!-- ── Results ──────────────────────────────────────────────────────────── -->
<section class="ia-index__results">
  <div class="ia-index__results-inner">
    <p class="ia-index__counter">
      <span class="ia-index__dot" aria-hidden="true"></span>
      <span class="ia-index__counter-live">
        {{ liveCounterKey | translate: { count: liveCount } }}
      </span>
      <!-- Withheld while loading: "0 sales shown" beside a grid of skeletons
           would be a wrong number, not a pending one. -->
      @if (!isLoading) {
        <span class="ia-index__counter-results">
          <span class="ia-index__counter-sep" aria-hidden="true">&#183;</span>
          <span>{{ resultsCounterKey | translate: { count: sales.length } }}</span>
        </span>
      }
    </p>

    @if (isLoading) {
      <div class="ia-index__grid" aria-hidden="true">
        @for (placeholder of skeletonCards; track placeholder) {
          <div class="ia-skeleton">
            <div class="ia-skeleton__band"></div>
            <div class="ia-skeleton__media"></div>
            <div class="ia-skeleton__body">
              <span class="ia-skeleton__line ia-skeleton__line--title"></span>
              <span class="ia-skeleton__line ia-skeleton__line--meta"></span>
              <span class="ia-skeleton__line ia-skeleton__line--footer"></span>
            </div>
          </div>
        }
      </div>
    } @else if (sales.length > 0) {
      <div class="ia-index__grid">
        @for (sale of sales; track sale.id) {
          <app-international-sale-card
            [sale]="sale"
            variant="grid"
          ></app-international-sale-card>
        }
      </div>
    } @else {
      <p class="ia-index__empty">
        {{ 'international_auctions.empty.sales' | translate }}
      </p>
    }

    <p class="ia-index__disclaimer">
      <span class="ia-index__disclaimer-mark" aria-hidden="true">&#10005;</span>
      <span>{{ 'international_auctions.disclaimer' | translate }}</span>
    </p>
  </div>
</section>
